import { create } from 'zustand';
import type { ChannelUnsubscribe } from 'shared/channels';
import { fieldValueClients } from 'shared/domain/fieldValue/fieldValueClients';
import type { EditableFieldValuesEntityName } from 'shared/domain/fieldValue/fields';
import type { FieldValueModel } from 'shared/domain/fieldValue/fieldValueModel';
import type { Message } from 'shared/domain/messages/message';
import { toFieldValueOnView } from 'presentation/fieldValue/mappings';
import { runWhenSwConfigReady } from 'services/serviceWorker/swConfigReady';
import { logInfo } from 'setup/logger/logInfo';
import { createSelectors, StoreName, withDevtools } from '../config';
import type { StoreData } from '../types';
import type { FieldValueOnView } from 'presentation/fieldValue/fieldValueOnView';
import { classifyEntityEvent, EntityEventReaction } from '../entityCollection';
import { areFieldValuesEqual, createInitialFieldValuesData } from './model';
import type { FieldValuesStore } from './types';

/** Event subscription per loaded dictionary; outside the state, it is not data. */
const subscriptions = new Map<EditableFieldValuesEntityName, ChannelUnsubscribe>();

const useFieldValuesStoreBase = create<FieldValuesStore>()(
  withDevtools((set, get) => {
    // An update that changes nothing keeps the state: a new `data` would regenerate every form
    // and remap the shown issue on each sync broadcast of an unchanged dictionary.
    const updateEntity = (
      entity: EditableFieldValuesEntityName,
      update: Partial<StoreData<FieldValueOnView>>
    ): void => {
      const current = get().data[entity];
      const changes = (Object.keys(update) as (keyof typeof update)[]).some(
        (key) => !Object.is(current[key], update[key])
      );
      if (!changes) {
        return;
      }
      set((state) => ({
        data: { ...state.data, [entity]: { ...state.data[entity], ...update } },
      }));
    };

    const applyItems = (entity: EditableFieldValuesEntityName, models: FieldValueModel[]): void => {
      const items = models.map(toFieldValueOnView);
      const isUnchanged = areFieldValuesEqual(get().data[entity].items, items);
      updateEntity(entity, isUnchanged ? { loading: false } : { items, loading: false });
    };

    const reload = async (entity: EditableFieldValuesEntityName): Promise<void> => {
      const startedIn = get().generation;
      const isCurrent = (): boolean => get().generation === startedIn;
      try {
        const models = await fieldValueClients[entity].getAll();
        if (isCurrent()) {
          applyItems(entity, models);
        }
      } catch (error) {
        // A timeout is already reported with diagnostics by the channel layer.
        logInfo('field-values-load-failed', { entity, error });
        if (isCurrent()) {
          updateEntity(entity, { loading: false });
        }
      }
    };

    const reloadWhenConfigReady = (entity: EditableFieldValuesEntityName): void =>
      runWhenSwConfigReady(() => void reload(entity));

    const handleEvent = (entity: EditableFieldValuesEntityName, event: Message): void => {
      const outcome = classifyEntityEvent(event, fieldValueClients[entity].readCollection);
      switch (outcome.reaction) {
        case EntityEventReaction.applyCollection:
          applyItems(entity, outcome.items);
          return;
        case EntityEventReaction.reload:
          reloadWhenConfigReady(entity);
          return;
        case EntityEventReaction.stopLoading:
          updateEntity(entity, { loading: false });
          return;
        case EntityEventReaction.ignore:
          return;
      }
    };

    return {
      data: createInitialFieldValuesData(),
      generation: 0,
      actions: {
        ensureLoaded: (entity) => {
          if (subscriptions.has(entity)) {
            return;
          }
          subscriptions.set(
            entity,
            fieldValueClients[entity].onEvent((event) => handleEvent(entity, event))
          );
          updateEntity(entity, { loading: true });
          reloadWhenConfigReady(entity);
        },
        reload,
        reloadLoaded: () => subscriptions.forEach((_, entity) => reloadWhenConfigReady(entity)),
        reset: () => {
          subscriptions.forEach((unsubscribe) => unsubscribe());
          subscriptions.clear();
          set((state) => ({ data: createInitialFieldValuesData(), generation: state.generation + 1 }));
        },
      },
    };
  }, StoreName.FieldValues)
);

export const useFieldValuesStore = createSelectors(useFieldValuesStoreBase);

export const getFieldValuesStoreState = (): FieldValuesStore => useFieldValuesStore.getState();
